import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";
import * as Yup from "yup";
import {FormikProps, useFormik} from "formik";
import {FormInput, FormInputLabel, Forms, FormSelect, notify, QrCode} from "@housebets/housebets-x-package";

import apiWallet from "app/api/wallet";
import {useAppSelector} from "hooks/useAppSelector";
import {getCurrencyIcon} from "app/utils/help";
import {useGTMEvents} from "app/hooks/useGTMEvents";

import {IDepositCryptoFormProps, TFormikConfig} from "./types";
import pkg from 'lodash';
const {debounce, sortBy} = pkg;

export const DepositCryptoForm: React.FC<IDepositCryptoFormProps> = ({category, onSubmit = () => {}}) => {
    const {$t} = useIntl();
    const {form} = useGTMEvents();
    const payProviderList = useAppSelector((state) => state.config.platformConfig?.payProviderList || []);

    const networkOpt = useMemo(() => {
        const channels = payProviderList.filter((x) => x.tokenId === category?.id && x.canDeposit === 1) || [];
        const res = sortBy(channels, (item) => item.listOrder)
            .map((x) => x.providerName)
            .map((v) => {
                return {
                    id: v,
                    name: v,
                };
            });
        return res || [];
    }, [category, payProviderList]);

    const initData: TFormikConfig = {
        network: networkOpt[0]?.id || "",
        address: "",
    };

    const formSchema = () =>
        Yup.object().shape({
            network: Yup.string().required($t({id: "FieldIsRequired"})),
            address: Yup.string().required($t({id: "FieldIsRequired"})),
        });

    const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
        formik.setValues({...initData});
        formik.setErrors({});
        formik.setTouched({});
        setError?.(false);
    };

    const [address, setAddress] = useState("");
    const [minAmount, setMinAmount] = useState("0.00");
    const [error, setError] = useState("");
    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {...initData},
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: () => {
            onSubmit();
        },
    });

    const cryptoChannels = useMemo(() => {
        const channels = payProviderList.filter((x) => x.tokenId === category?.id && x.canDeposit === 1) || [];
        return sortBy(channels, (item) => item.listOrder).map((x) => x.providerName);
    }, [category, payProviderList]);

    const realAddress = useMemo(() => {
        return address.split("@@@")[0];
    }, [address]);

    const reqeustAddress = async (id: number, network: string): Promise<void> => {
        try {
            setAddress("");
            formik.setFieldValue("address", "");
            const {status, data} = await apiWallet.getWalletAddress(id, network);
            if (status === 200 && data.code === 0) {
                formik.setFieldValue("address", data.data);
                setAddress(data.data || "");
            } else {
                formik.setFieldValue("address", "");
                setAddress("");
            }
        } catch (error) {
            console.error(error);
            // empty
        }
    };

    const debounceFnRequestAddress = useCallback(debounce(reqeustAddress, 25), []);

    useEffect(() => {
        const network = formik.values.network + "";
        if (cryptoChannels.includes(network)) {
            debounceFnRequestAddress(Number(category?.id), network);
        }

        const updateMinDeposit = () => {
            const minDeposit = payProviderList.find((v) => v.tokenId === category?.id && v.providerName === network);
            setMinAmount(minDeposit?.minDeposit + "");
        };
        updateMinDeposit();
    }, [category, formik.values.network]);

    useEffect(() => {
        initForm(formik);
    }, [category]);

    useEffect(() => {
        const keys = Object.keys(formik.errors);
        keys.forEach((item) => {
            form.error({
                field: item,
                message: formik.errors[item as keyof TFormikConfig] as string,
                form_id: "deposit_crypto_form",
                form_name: "Deposit Crypto Form",
            });
        });
    }, [formik.errors]);

    return (
        <Forms.WalletForm.Wrapper title={$t({id: "DepositCrypto"})}>
            <form id="deposit_crypto_form" onSubmit={formik.handleSubmit}>
                <Forms.WalletForm.FormRow>
                    <FormSelect
                        size="sm"
                        options={networkOpt}
                        placeholder={$t({id: "Network"})}
                        label={$t({id: "Network"})}
                        name="network"
                        required
                        errorMsgAbsolute
                        value={formik.values?.network as string}
                        errors={
                            (!!formik.errors?.network && !!formik.touched?.network) || error
                                ? {
                                      message: formik.errors?.network || error,
                                  }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("network", val[0])}
                        onFocus={() => {
                            formik.setFieldTouched("network", false);
                            formik.setFieldTouched("network", false);
                            formik.setFieldTouched("network", false);
                        }}
                        onBlur={() => {
                            formik.setFieldTouched("network", true);
                            formik.setFieldTouched("network", true);
                            formik.setFieldTouched("network", true);
                        }}
                    />
                </Forms.WalletForm.FormRow>
                <Forms.WalletForm.FormRow>
                    <FormInput
                        size="sm"
                        placeholder={$t({id: "Address"})}
                        name="address"
                        type="text"
                        errorMsgAbsolute
                        disabled
                        label={<FormInputLabel blueBold>{$t({id: "YourPersonalDepositAddressA"}, {A: <span>{formik.values?.network}</span>})}</FormInputLabel>}
                        value={formik.values?.address}
                        errors={
                            (!!formik.errors?.address && !!formik.touched?.address) || error
                                ? {
                                      message: formik.errors?.address || error,
                                  }
                                : null
                        }
                        copyButton={{
                            onClick: () => {
                                notify({
                                    type: "success",
                                    title: "Copied",
                                });
                            },
                        }}
                    />
                </Forms.WalletForm.FormRow>
                <Forms.WalletForm.FormRow>
                    {realAddress ? (
                        <QrCode
                            style={{padding: 16}}
                            currency={getCurrencyIcon(category?.id || 0)}
                            minAmount={minAmount}
                            svgData={realAddress}
                            img=""
                            whiteBackground
                        />
                    ) : null}
                </Forms.WalletForm.FormRow>
                <Forms.WalletForm.FormRow>
                    <Forms.WalletForm.Warning
                        text={{
                            warning: $t({id: "Warning"}),
                            body: $t({id: "PleaseVerifyAddressBeforeTransaction"}),
                        }}
                    />
                </Forms.WalletForm.FormRow>
            </form>
        </Forms.WalletForm.Wrapper>
    );
};

DepositCryptoForm.displayName = "DepositCryptoForm";
